import Image from 'next/image';
import clsx from 'clsx';
import React, { useContext, useRef } from 'react';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { useRadio, useRadioGroup } from '@react-aria/radio';
import { useFocusRing } from '@react-aria/focus';
import { mergeProps } from '@react-aria/utils';
import { useRadioGroupState } from '@react-stately/radio';
import { onEnterPressRadio } from '@/utils/events';
import visaImg from '../../../../public/assets/Visa.png';
import payIdImg from '../../../../public/assets/PayID.png';
import bankTransferImg from '../../../../public/assets/BankTransfer.png';
import styles from './SelectPaymentCard.module.css';
import { Icon, RadioContext, RadioGroupProps, RadioProps } from '../../../../components/common';

export const SelectPaymentCardGroup = ({ className, ariaLabel, ...props }: RadioGroupProps) => {
  const { children } = props;
  const state = useRadioGroupState(props);
  const { radioGroupProps } = useRadioGroup({ ...props, 'aria-label': ariaLabel }, state);

  return (
    <div {...radioGroupProps} className={clsx(styles.group, className)}>
      <RadioContext.Provider value={state}>{children}</RadioContext.Provider>
    </div>
  );
};

export interface RadioSelectAccountProps {
  type: string;
}

export const SelectPaymentCard = ({ className, type, isDisabled, ...props }: RadioProps & RadioSelectAccountProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const state = useContext(RadioContext);
  const { inputProps } = useRadio(
    {
      ...props,
      onKeyUp: onEnterPressRadio(props, state, props.value),
    },
    state,
    ref,
  );
  const { focusProps, isFocusVisible: isFocused } = useFocusRing();

  const renderImage = (type: string) => {
    switch (type) {
      case 'Bank Transfer':
        return <Image width={40} height={40} src={bankTransferImg} alt={type} priority loading="eager" />;
      case 'Debit Card':
        return <Image width={60} height={14} src={visaImg} alt={type} priority loading="eager" />;
      default:
        return <Image width={50} height={24} src={payIdImg} alt={type} priority loading="eager" />;
    }
  };

  return (
    <>
      <VisuallyHidden>
        <input {...mergeProps(inputProps, focusProps)} className={styles.input} ref={ref} type="radio" />
      </VisuallyHidden>
      <button
        className={clsx(styles.wrapper, className)}
        type={!isDisabled ? 'submit' : 'button'}
        onClick={() => ref.current?.click()}
      >
        <div className={clsx(styles.card, { [styles.disabled]: isDisabled })}>
          <div className={styles.cardName}>
            <div
              className={clsx(styles.imageWrapper, {
                [styles.disabledImageWrapper]: isDisabled,
              })}
            >
              {renderImage(type)}
            </div>
            <span>{type}</span>
          </div>
          <div className={styles.iconWrapper}>
            {isDisabled && <span className={styles.extraInfo}>SOON</span>}
            <Icon name="chevronRight" size={24} />
          </div>
        </div>
      </button>
    </>
  );
};

export default SelectPaymentCard;
